// ============================================================================
// AUTHENTICATION HOOKS
// ============================================================================
// React hooks for Authentication and User Management

import { useState, useEffect, useCallback } from 'react';
import { authService } from 'api/services';
import { tokenUtils } from 'api/config/apiConfig';
import { rolePermissionsService } from 'api/services/admin/rolePermissionsService';
import { buildFullPermissionsFromCatalog, isSuperRole } from 'config/rolePermissions';
import type {
  User,
  LoginRequest,
  RegisterRequest,
  ChangePasswordRequest,
  ForgotPasswordRequest,
  ResetPasswordRequest,
  UpdateProfileRequest,
  DiscoverRequest,
  ID,
} from 'api/types';

/**
 * Thrown when authentication succeeds but the JWT carries no recognised DAS
 * role code. The login screen catches this and displays the message in its
 * existing error-alert region (same UI path as a Keycloak failure).
 */
export class LoginRejectedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'LoginRejectedError';
    // Restore prototype chain for `err instanceof LoginRejectedError`.
    Object.setPrototypeOf(this, LoginRejectedError.prototype);
  }
}

/**
 * Hook to handle user login using Tenant Hub two-step authentication
 */
export const useLogin = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);

  const login = async (
    credentials: LoginRequest | DiscoverRequest,
    options?: {
      onSuccess?: (user: User) => void;
      onError?: (error: Error) => void;
    }
  ) => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      // Use Tenant Hub two-step authentication
      const response = await authService.loginWithTenantHub({
        username: credentials.username,
        password: credentials.password,
      });

      if (response.success && response.data) {
        // Build user from flat properties in response (backend returns flat structure)
        const data = response.data;
        const user: User = {
          id: data.userId || '',
          username: data.userName || credentials.username,
          email: data.userEmail || '',
          firstName: data.userFirstName || '',
          lastName: data.userLastName || '',
          fullName:
            `${data.userFirstName || ''} ${data.userLastName || ''}`.trim() ||
            data.userName ||
            credentials.username,
          roles: data.permissions?.roles || [],
          permissions: data.permissions?.permissions || [],
        };

        // ── ROLE GATE ─────────────────────────────────────────────────────
        // Pass the gate when the user holds ANY das-client role (i.e. the
        // JWT carried at least one entry under resource_access.das.roles —
        // see authService.extractRolesFromToken). We deliberately do NOT
        // check against a hardcoded allow-list of role codes here:
        //   - Admins can create new DAS roles in Tenant Hub (Keycloak)
        //     at any time (e.g. das_supervisor, das_auditor, ...) and the
        //     gate must accept them automatically without a code change.
        //   - The role's actual access is governed by its row in
        //     admin.tbl_role_permission (configured via the Role
        //     Permissions admin UI). If no row exists, the merged
        //     permissions are empty → the user lands on Overview with
        //     nothing else visible; that's correct behaviour and signals
        //     the admin to configure permissions for the new role.
        // The only thing we reject here is a user with NO das role at all —
        // such users haven't been granted access to the DAS application by
        // Keycloak and shouldn't reach the dashboard.
        const roleCodes = (user.roles ?? []).map((r) => r.code).filter(Boolean);
        if (roleCodes.length === 0) {
          tokenUtils.clearTokens();
          throw new LoginRejectedError(
            'No DAS roles are assigned to this account. Please contact the administrator.'
          );
        }

        // ── ROLE-PERMISSIONS LOOKUP ──────────────────────────────────────
        // Fetch the merged visible_modules + module_actions for this user's
        // role codes so main_layout can filter the sidebar + thread per-
        // module action lists into each rendered screen.
        //
        // SUPER-ROLE OVERRIDE: when the user has das_system we ignore the
        // stored row and grant the FULL catalog from ALL_MODULES. This
        // guarantees that any new screen registered in the frontend is
        // available to das_system immediately — without an admin editing
        // the seed or re-running a migration. das_system also cannot be
        // edited via the UI (RolePermissionModal enforces read-only).
        //
        // On non-super-role failures we fall back to empty merged
        // permissions (no access — every nav item hidden). We never
        // silently grant full access on error for non-super users.
        if (isSuperRole(roleCodes)) {
          user.rolePermissions = buildFullPermissionsFromCatalog();
        } else {
          try {
            const merged = await rolePermissionsService.getByRoles(roleCodes.join(','));
            user.rolePermissions = merged;
          } catch (permErr) {
            console.error(
              '[useAuth] Failed to load role permissions; falling back to empty set.',
              permErr
            );
            user.rolePermissions = { visibleModules: [], moduleActions: {} };
          }
        }

        // Persist the enriched user (with rolePermissions) so that
        // useCurrentUser — which reads from session storage on page reload —
        // sees the role-permissions payload. authService.authenticate()
        // wrote a slimmer userData earlier in the flow without these fields.
        tokenUtils.setUserData(user);

        setSuccess(true);
        options?.onSuccess?.(user);
        return response;
      } else {
        throw new Error(response.message || 'Authentication failed');
      }
    } catch (err) {
      const error = err as Error;
      setError(error);
      options?.onError?.(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return {
    login,
    loading,
    error,
    success,
  };
};

/**
 * Hook to handle user logout
 */
export const useLogout = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const logout = async (
    options?: {
      onSuccess?: () => void;
      onError?: (error: Error) => void;
    }
  ) => {
    setLoading(true);
    setError(null);
    try {
      await authService.logout();
      options?.onSuccess?.();
    } catch (err) {
      const error = err as Error;
      setError(error);
      options?.onError?.(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return {
    logout,
    loading,
    error,
  };
};

/**
 * Hook to handle user registration
 */
export const useRegister = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);

  const register = async (
    data: RegisterRequest,
    options?: {
      onSuccess?: (user: User) => void;
      onError?: (error: Error) => void;
    }
  ) => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      const response = await authService.register(data);
      setSuccess(true);
      options?.onSuccess?.(response.data.user);
      return response.data;
    } catch (err) {
      const error = err as Error;
      setError(error);
      options?.onError?.(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return {
    register,
    loading,
    error,
    success,
  };
};

/**
 * Hook to get current user
 * Uses stored user data from session storage as primary source,
 * with optional API fetch to refresh the data
 */
export const useCurrentUser = () => {
  // Initialize from session storage for immediate display
  const [data, setData] = useState<User | null>(() => {
    return tokenUtils.getUserData<User>();
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchCurrentUser = useCallback(async () => {
    if (!authService.isAuthenticated()) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await authService.getCurrentUser();
      if (response.data) {
        setData(response.data);
        // Update stored user data
        tokenUtils.setUserData(response.data);
      }
    } catch (err) {
      // On API error, keep the stored user data if available
      const storedUser = tokenUtils.getUserData<User>();
      if (storedUser) {
        setData(storedUser);
      }
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Optionally fetch fresh data from API (can be disabled if not needed)
  useEffect(() => {
    // Only fetch if we don't have stored data or want to refresh
    if (authService.isAuthenticated() && !data) {
      fetchCurrentUser();
    }
  }, [fetchCurrentUser, data]);

  return {
    user: data,
    loading,
    error,
    refetch: fetchCurrentUser,
    isAuthenticated: authService.isAuthenticated(),
  };
};

/**
 * Hook to update user profile
 */
export const useUpdateProfile = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);

  const updateProfile = async (
    data: UpdateProfileRequest,
    options?: {
      onSuccess?: (user: User) => void;
      onError?: (error: Error) => void;
    }
  ) => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      const response = await authService.updateProfile(data);
      setSuccess(true);
      options?.onSuccess?.(response.data);
      return response.data;
    } catch (err) {
      const error = err as Error;
      setError(error);
      options?.onError?.(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return {
    updateProfile,
    loading,
    error,
    success,
  };
};

/**
 * Hook to change password
 */
export const useChangePassword = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);

  const changePassword = async (
    data: ChangePasswordRequest,
    options?: {
      onSuccess?: () => void;
      onError?: (error: Error) => void;
    }
  ) => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      await authService.changePassword(data);
      setSuccess(true);
      options?.onSuccess?.();
    } catch (err) {
      const error = err as Error;
      setError(error);
      options?.onError?.(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return {
    changePassword,
    loading,
    error,
    success,
  };
};

/**
 * Hook to request password reset
 */
export const useForgotPassword = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);

  const forgotPassword = async (
    data: ForgotPasswordRequest,
    options?: {
      onSuccess?: () => void;
      onError?: (error: Error) => void;
    }
  ) => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      await authService.forgotPassword(data);
      setSuccess(true);
      options?.onSuccess?.();
    } catch (err) {
      const error = err as Error;
      setError(error);
      options?.onError?.(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return {
    forgotPassword,
    loading,
    error,
    success,
  };
};

/**
 * Hook to reset password
 */
export const useResetPassword = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);

  const resetPassword = async (
    data: ResetPasswordRequest,
    options?: {
      onSuccess?: () => void;
      onError?: (error: Error) => void;
    }
  ) => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      await authService.resetPassword(data);
      setSuccess(true);
      options?.onSuccess?.();
    } catch (err) {
      const error = err as Error;
      setError(error);
      options?.onError?.(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return {
    resetPassword,
    loading,
    error,
    success,
  };
};

/**
 * Hook to get all users (admin)
 */
export const useUsers = (params?: any) => {
  const [data, setData] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await authService.getUsers(params);
      setData(response.data);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  return {
    data,
    loading,
    error,
    refetch: fetchUsers,
  };
};

/**
 * Hook for authentication state
 */
export const useAuth = () => {
  const { user, loading, error, refetch, isAuthenticated } = useCurrentUser();
  const { login, loading: loginLoading } = useLogin();
  const { logout, loading: logoutLoading } = useLogout();

  return {
    user,
    loading: loading || loginLoading || logoutLoading,
    error,
    isAuthenticated,
    login,
    logout,
    refetch,
  };
};
